import type {
  ObservabilityRumSessionDetailResponse,
  ObservabilityRumSessionSearchItem,
  ObservabilityRumSessionSearchResponse,
  ObservabilityRumWebVitalSummary,
} from '@scatterin/api-types';
import { ArrowLeft, ArrowRight, ExternalLink, MonitorSmartphone, ScrollText, TriangleAlert } from 'lucide-react';
import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router';

import { CodeBlock, JsonBlock } from '@/components/codeBlock';
import { DetailList } from '@/components/keyValueGrid';
import { EmptyState, ErrorState, LoadingState, StateView } from '@/components/stateViews';
import { StatusBadge } from '@/components/statusBadges';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Drawer } from '@/components/ui/drawer';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow, TableShell } from '@/components/ui/table';
import { useSessionDetailQuery, useSessionSearchQuery, useSummaryQuery } from '@/hooks/useObservabilityQueries';
import { useRouteContext } from '@/hooks/useRouteContext';
import { formatCount, formatDuration, formatInteger, formatRelativeTime, formatTime } from '@/lib/format';
import { drillThroughSearch } from '@/lib/navigation';
import type { AnalyticsUrlState } from '@/lib/urlState';
type VitalRating = 'good' | 'needs improvement' | 'poor' | 'unknown';
type QueryView<T> = {
  data?: T;
  error: Error | null;
  isError: boolean;
  isLoading: boolean;
  refetch: () => unknown;
};

const vitalThresholds: Record<string, { good: number; poor: number; unit: string }> = {
  CLS: { good: 0.1, poor: 0.25, unit: '' },
  INP: { good: 200, poor: 500, unit: 'ms' },
  LCP: { good: 2500, poor: 4000, unit: 'ms' },
};

const updateSearch = (current: string, updates: Record<string, string | null>): string => {
  const search = new URLSearchParams(current);
  for (const [key, value] of Object.entries(updates)) {
    if (value) search.set(key, value);
    else search.delete(key);
  }
  return search.toString();
};

const ratingFor = (metricName: string, value: number | null | undefined): VitalRating => {
  const threshold = vitalThresholds[metricName];
  if (!threshold || typeof value !== 'number') return 'unknown';
  if (value <= threshold.good) return 'good';
  if (value <= threshold.poor) return 'needs improvement';
  return 'poor';
};

const ratingTone = (rating: VitalRating): 'amber' | 'green' | 'red' | 'slate' => {
  if (rating === 'good') return 'green';
  if (rating === 'needs improvement') return 'amber';
  if (rating === 'poor') return 'red';
  return 'slate';
};

const formatVital = (metricName: string, value: number | null | undefined): string => {
  if (typeof value !== 'number') return 'Not recorded';
  const unit = vitalThresholds[metricName]?.unit;
  if (unit === 'ms') return formatDuration(value, 'ms');
  return value.toLocaleString(undefined, { maximumFractionDigits: 3 });
};

const sessionIdentity = (item: ObservabilityRumSessionSearchItem): string => item.session.userId || item.session.sessionId;

const browserLabel = (item: ObservabilityRumSessionSearchItem): string =>
  [item.session.browser, item.session.browserVersion, item.session.os].filter(Boolean).join(' · ') || 'Browser not recorded';

const VitalHealth = ({ summaries }: { summaries: ObservabilityRumWebVitalSummary[] }) => {
  const core = summaries
    .filter((summary) => ['LCP', 'INP', 'CLS'].includes(summary.metricName))
    .toSorted((left, right) => {
      const route = (left.route ?? '').localeCompare(right.route ?? '');
      return route || left.metricName.localeCompare(right.metricName);
    });

  if (core.length === 0) {
    return (
      <EmptyState title="No Core Web Vital observations" tone="coverage">
        Route health needs recorded LCP, INP, or CLS events in this scope.
      </EmptyState>
    );
  }

  return (
    <div className="grid gap-3">
      {core.map((summary) => {
        const rating = ratingFor(summary.metricName, summary.p75);
        return (
          <article className="border-line grid gap-3 rounded-lg border p-4" key={`${summary.metricName}:${summary.route ?? 'all'}`}>
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <p className="text-ink font-semibold">{summary.route ?? 'All routes'}</p>
                <p className="text-muted mt-1 text-xs">{summary.metricName} at P75</p>
              </div>
              <Badge tone={ratingTone(rating)}>{rating}</Badge>
            </div>
            <div
              aria-label={`${summary.route ?? 'All routes'} ${summary.metricName}: P75 ${formatVital(summary.metricName, summary.p75)}, ${rating}, ${formatCount(summary.values.count, 'sample')}`}
              className="grid grid-cols-2 gap-3 sm:grid-cols-4"
              role="img"
            >
              <div>
                <p className="text-muted text-xs font-semibold">P75</p>
                <p className="text-ink numeric mt-1 font-semibold">{formatVital(summary.metricName, summary.p75)}</p>
              </div>
              <div>
                <p className="text-muted text-xs font-semibold">P50</p>
                <p className="text-ink numeric mt-1">{formatVital(summary.metricName, summary.values.p50)}</p>
              </div>
              <div>
                <p className="text-muted text-xs font-semibold">P95</p>
                <p className="text-ink numeric mt-1">{formatVital(summary.metricName, summary.values.p95)}</p>
              </div>
              <div>
                <p className="text-muted text-xs font-semibold">Samples</p>
                <p className="text-ink numeric mt-1">{formatInteger(summary.values.count)}</p>
              </div>
            </div>
          </article>
        );
      })}
      <p className="text-muted text-xs">
        Good / needs improvement / poor uses the published metric thresholds against the observed P75. It describes measured experience, not
        the cause of degradation.
      </p>
    </div>
  );
};

const SessionCard = ({ currentSearch, item }: { currentSearch: string; item: ObservabilityRumSessionSearchItem }) => (
  <article className="border-line grid min-w-0 gap-3 rounded-lg border p-4">
    <div className="flex flex-wrap items-center justify-between gap-2">
      <Badge tone={(item.clientErrors ?? 0) > 0 ? 'red' : 'slate'}>
        {(item.clientErrors ?? 0) > 0 ? `${formatInteger(item.clientErrors)} client errors` : 'No client errors'}
      </Badge>
      <span className="text-muted text-xs">{formatRelativeTime(item.session.lastSeenAt)}</span>
    </div>
    <div className="min-w-0">
      <p className="text-ink truncate font-semibold">{sessionIdentity(item)}</p>
      <p className="text-muted mt-1 text-sm">{browserLabel(item)}</p>
    </div>
    <dl className="border-line grid grid-cols-2 gap-3 border-t pt-3 text-sm">
      <div>
        <dt className="text-muted text-xs font-semibold">Last route</dt>
        <dd className="text-ink mt-1 truncate">{item.lastRoute || 'Not recorded'}</dd>
      </div>
      <div>
        <dt className="text-muted text-xs font-semibold">Evidence</dt>
        <dd className="text-ink numeric mt-1">
          {formatCount(item.pageViews, 'view')} · {formatCount(item.webVitals, 'vital')}
        </dd>
      </div>
    </dl>
    <Button asChild variant="secondary">
      <Link
        to={{
          pathname: '/frontend-experience',
          search: updateSearch(currentSearch, { sessionId: item.session.sessionId }),
        }}
      >
        Inspect session
      </Link>
    </Button>
  </article>
);

const Sessions = ({
  currentSearch,
  query,
  setState,
  state,
}: {
  currentSearch: string;
  query: QueryView<ObservabilityRumSessionSearchResponse>;
  setState: (next: Partial<AnalyticsUrlState>) => void;
  state: AnalyticsUrlState;
}) => {
  const [searchValue, setSearchValue] = useState(state.search);
  const items = query.data?.items ?? [];
  const filtered =
    Boolean(state.search) ||
    state.sessionRoutes.length > 0 ||
    state.sessionBrowsers.length > 0 ||
    state.sessionDevices.length > 0 ||
    state.sessionVitalNames.length > 0 ||
    state.sessionErrorStatus !== 'all';

  return (
    <section className="grid min-w-0 gap-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="text-ink text-base font-semibold">Browser sessions</h2>
          <p className="text-muted mt-1 text-sm">Discover a session, then inspect its recorded journey and correlations.</p>
        </div>
        <p className="text-muted text-xs">
          Page {Math.floor(state.sessionOffset / state.sessionPageSize) + 1} · up to {formatInteger(state.sessionPageSize)}
        </p>
      </div>
      <form
        className="grid gap-3 lg:grid-cols-[minmax(16rem,1fr)_auto]"
        onSubmit={(event) => {
          event.preventDefault();
          setState({ search: searchValue, sessionId: null, sessionOffset: 0 });
        }}
      >
        <label className="text-muted grid gap-1.5 text-xs font-semibold">
          Find a session
          <span className="flex gap-2">
            <input
              aria-label="Find a session"
              className="border-line bg-raised text-ink placeholder:text-muted/70 focus:border-accent focus:ring-accent/20 min-h-10 min-w-0 flex-1 rounded-md border px-3 text-sm outline-none focus:ring-2"
              onChange={(event) => setSearchValue(event.target.value)}
              placeholder="Session ID, user, browser, device, or operating system"
              type="search"
              value={searchValue}
            />
            <Button type="submit" variant="secondary">
              Search
            </Button>
          </span>
        </label>
      </form>

      {query.isLoading ? <LoadingState label="Loading browser sessions" /> : null}
      {query.isError ? <ErrorState message={query.error?.message ?? 'Session search failed'} onRetry={() => void query.refetch()} /> : null}
      {query.data && items.length === 0 ? (
        <EmptyState
          title={filtered ? 'No sessions match these filters' : 'No browser sessions were recorded in this window'}
          tone={filtered ? 'filtered' : 'coverage'}
        >
          {filtered
            ? 'Clear the session search or advanced filters, or widen the selected period.'
            : 'This absence is not presented as healthy without browser collection evidence.'}
        </EmptyState>
      ) : null}
      {items.length > 0 ? (
        <Card>
          <CardContent className="min-w-0">
            <div className="grid gap-3 md:hidden">
              {items.map((item) => (
                <SessionCard currentSearch={currentSearch} item={item} key={item.session.sessionId} />
              ))}
            </div>
            <TableShell className="hidden md:block">
              <Table aria-label="Discoverable browser session list" className="table-fixed" style={{ minWidth: '58rem' }}>
                <TableHeader>
                  <TableRow>
                    <TableHead>Identity / browser</TableHead>
                    <TableHead className="w-44">Last route</TableHead>
                    <TableHead className="w-36">Client errors</TableHead>
                    <TableHead className="w-36">Evidence</TableHead>
                    <TableHead className="w-40">Last seen</TableHead>
                    <TableHead className="w-28 text-right">Inspect</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {items.map((item) => (
                    <TableRow key={item.session.sessionId}>
                      <TableCell>
                        <p className="truncate font-semibold">{sessionIdentity(item)}</p>
                        <p className="text-muted mt-1 truncate text-xs">{browserLabel(item)}</p>
                      </TableCell>
                      <TableCell className="truncate">{item.lastRoute || 'Not recorded'}</TableCell>
                      <TableCell>
                        <Badge tone={(item.clientErrors ?? 0) > 0 ? 'red' : 'slate'}>{formatInteger(item.clientErrors)}</Badge>
                      </TableCell>
                      <TableCell className="numeric text-xs">
                        {formatCount(item.pageViews, 'view')} · {formatCount(item.webVitals, 'vital')}
                      </TableCell>
                      <TableCell>
                        <p>{formatRelativeTime(item.session.lastSeenAt)}</p>
                        <p className="text-muted mt-1 text-xs">{formatTime(item.session.lastSeenAt)}</p>
                      </TableCell>
                      <TableCell className="text-right">
                        <Button asChild variant="ghost">
                          <Link
                            to={{
                              pathname: '/frontend-experience',
                              search: updateSearch(currentSearch, { sessionId: item.session.sessionId }),
                            }}
                          >
                            Details
                          </Link>
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableShell>
          </CardContent>
        </Card>
      ) : null}
      {query.data ? (
        <div className="flex items-center justify-between gap-3">
          <Button
            disabled={state.sessionOffset === 0}
            onClick={() =>
              setState({
                sessionId: null,
                sessionOffset: Math.max(state.sessionOffset - state.sessionPageSize, 0),
              })
            }
            variant="secondary"
          >
            <ArrowLeft aria-hidden="true" className="size-4" />
            Previous
          </Button>
          <Button
            disabled={!query.data.hasMore}
            onClick={() =>
              setState({
                sessionId: null,
                sessionOffset: state.sessionOffset + state.sessionPageSize,
              })
            }
            variant="secondary"
          >
            Next
            <ArrowRight aria-hidden="true" className="size-4" />
          </Button>
        </div>
      ) : null}
    </section>
  );
};

type TimelineEvent = {
  id: string;
  kind: 'error' | 'navigation' | 'page view' | 'vital';
  observedAt: string;
  route: string;
  summary: string;
  stackTrace?: string;
};

const stackTraceFrom = (attributes: Record<string, unknown> | undefined): string | undefined => {
  const stackTrace = attributes?.stackTrace;
  return typeof stackTrace === 'string' && stackTrace.length > 0 ? stackTrace : undefined;
};

const sessionTimeline = (detail: NonNullable<ReturnType<typeof useSessionDetailQuery>['data']>): TimelineEvent[] =>
  [
    ...(detail.pageViews ?? []).map((event) => ({
      id: `page:${event.id}`,
      kind: 'page view' as const,
      observedAt: event.observedAt,
      route: event.route,
      summary: `Loaded in ${typeof event.loadDurationMs === 'number' ? formatDuration(event.loadDurationMs, 'ms') : 'an unrecorded duration'}`,
    })),
    ...(detail.routeNavigations ?? []).map((event) => ({
      id: `navigation:${event.id}`,
      kind: 'navigation' as const,
      observedAt: event.observedAt,
      route: event.toRoute,
      summary: `${event.fromRoute ?? 'Unknown route'} → ${event.toRoute}${
        typeof event.durationMs === 'number' ? ` in ${formatDuration(event.durationMs, 'ms')}` : ''
      }`,
    })),
    ...(detail.clientErrors ?? []).map((event) => ({
      id: `error:${event.id}`,
      kind: 'error' as const,
      observedAt: event.observedAt,
      route: event.route ?? 'Unknown route',
      summary: event.message ?? event.errorType,
      stackTrace: stackTraceFrom(event.attributes),
    })),
    ...(detail.webVitals ?? []).map((event) => ({
      id: `vital:${event.id}`,
      kind: 'vital' as const,
      observedAt: event.observedAt,
      route: event.route ?? 'All routes',
      summary: `${event.metricName} ${formatVital(event.metricName, event.value)} · ${ratingFor(event.metricName, event.value)}`,
    })),
  ].toSorted((left, right) => new Date(left.observedAt).getTime() - new Date(right.observedAt).getTime());

const SessionDetailDrawer = ({
  close,
  currentSearch,
  query,
  sessionId,
}: {
  close: () => void;
  currentSearch: string;
  query: QueryView<ObservabilityRumSessionDetailResponse>;
  sessionId: string | null;
  // Detail rendering intentionally keeps factual evidence branches colocated.
  // eslint-disable-next-line sonarjs/cognitive-complexity
}) => {
  const detail = query.data;
  const session = detail?.session;
  const events = detail ? sessionTimeline(detail) : [];
  const runIds = [
    ...new Set((detail?.failures ?? []).map((failure) => failure.runId).filter((runId): runId is string => typeof runId === 'string')),
  ];

  return (
    <Drawer
      description={
        session
          ? `${session.browser ?? 'Unknown browser'} · last seen ${formatRelativeTime(session.lastSeenAt)}`
          : 'Recorded session evidence'
      }
      onOpenChange={(open) => {
        if (!open) close();
      }}
      open={Boolean(sessionId)}
      side="right"
      size="lg"
      title={session ? session.userId || 'Browser session' : 'Session details'}
    >
      {query.isLoading ? <LoadingState label="Loading session detail" /> : null}
      {query.isError ? <ErrorState message={query.error?.message ?? 'Session detail failed'} onRetry={() => void query.refetch()} /> : null}
      {detail && !session ? (
        <EmptyState title="Session not found in this scope" tone="filtered">
          Close this detail view and adjust the date, environment, or session filters.
        </EmptyState>
      ) : null}
      {detail && session ? (
        <div className="grid gap-6">
          <div className="flex flex-wrap gap-2">
            <Badge tone={(detail.clientErrors ?? []).length > 0 ? 'red' : 'slate'}>
              {formatInteger(detail.clientErrors?.length)} client errors
            </Badge>
            <Badge tone="slate">{session.device || 'Device not recorded'}</Badge>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button asChild variant="secondary">
              <Link
                to={{
                  pathname: '/frontend-experience',
                  search: updateSearch(currentSearch, { sessionId: session.sessionId }),
                }}
              >
                <ExternalLink aria-hidden="true" className="size-4" />
                Share session
              </Link>
            </Button>
          </div>
          <section className="grid gap-3">
            <h3 className="text-ink text-sm font-semibold">Session context</h3>
            <DetailList
              items={[
                { key: 'session', label: 'Session ID', value: session.sessionId },
                { key: 'user', label: 'User', value: session.userId || 'Not recorded' },
                {
                  key: 'browser',
                  label: 'Browser',
                  value: [session.browser, session.browserVersion].filter(Boolean).join(' ') || 'Not recorded',
                },
                { key: 'device', label: 'Device / OS', value: [session.device, session.os].filter(Boolean).join(' · ') || 'Not recorded' },
                { key: 'started', label: 'Started', value: formatTime(session.startedAt) },
                { key: 'ended', label: 'Ended', value: session.endedAt ? formatTime(session.endedAt) : 'Not recorded' },
              ]}
            />
          </section>
          <section className="grid gap-3">
            <h3 className="text-ink text-sm font-semibold">Recorded journey</h3>
            {events.length === 0 ? (
              <EmptyState title="No session events were recorded" tone="coverage" />
            ) : (
              <ol className="border-line divide-y rounded-lg border">
                {events.map((event) => (
                  <li className={event.kind === 'error' ? 'bg-critical-wash grid gap-1 p-3' : 'grid gap-1 p-3'} key={event.id}>
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <span className={event.kind === 'error' ? 'text-critical text-sm font-semibold' : 'text-ink text-sm font-semibold'}>
                        {event.kind === 'error' ? 'Client error' : event.kind}
                      </span>
                      <span className="text-muted text-xs">{formatTime(event.observedAt)}</span>
                    </div>
                    <p className="text-ink text-sm">{event.summary}</p>
                    <p className="text-muted text-xs">{event.route}</p>
                    {event.stackTrace ? (
                      <details className="mt-2">
                        <summary className="text-critical cursor-pointer text-xs font-semibold">Readable stack trace</summary>
                        <div className="mt-2">
                          <CodeBlock value={event.stackTrace} />
                        </div>
                      </details>
                    ) : null}
                  </li>
                ))}
              </ol>
            )}
          </section>
          <section className="grid gap-3">
            <h3 className="text-ink text-sm font-semibold">Linked failures and logs</h3>
            {(detail.failures ?? []).length === 0 ? (
              <EmptyState title="No backend failure is linked to this session">
                Correlation is absent; the browser evidence is not assigned a backend cause.
              </EmptyState>
            ) : (
              <div className="grid gap-2">
                {(detail.failures ?? []).map((failure) => (
                  <article className="border-line rounded-lg border p-3" key={failure.id}>
                    <div className="flex flex-wrap items-start justify-between gap-2">
                      <div>
                        <p className="text-ink text-sm font-semibold">{failure.message ?? failure.errorType ?? failure.category}</p>
                        <p className="text-muted mt-1 text-xs">{formatTime(failure.detectedAt)}</p>
                      </div>
                      <StatusBadge value={failure.status} />
                    </div>
                    <div className="mt-3 flex flex-wrap gap-2">
                      <Button asChild variant="secondary">
                        <Link
                          to={{
                            pathname: '/failures',
                            search: drillThroughSearch(currentSearch, { failureId: failure.id, queueView: 'all' }),
                          }}
                        >
                          Failure detail
                        </Link>
                      </Button>
                      <Button asChild variant="secondary">
                        <Link
                          to={{
                            pathname: '/explore',
                            search: drillThroughSearch(currentSearch, { failureId: failure.id, view: 'logs' }),
                          }}
                        >
                          <ScrollText aria-hidden="true" className="size-4" />
                          Contextual logs
                        </Link>
                      </Button>
                    </div>
                  </article>
                ))}
              </div>
            )}
          </section>
          <section className="grid gap-3">
            <h3 className="text-ink text-sm font-semibold">Linked backend runs</h3>
            {runIds.length === 0 ? (
              <EmptyState title="No backend run correlation is recorded">
                A temporal overlap is not treated as a causal or factual link.
              </EmptyState>
            ) : (
              <div className="flex flex-wrap gap-2">
                {runIds.map((runId) => (
                  <Button asChild key={runId} variant="secondary">
                    <Link
                      to={{
                        pathname: '/performance',
                        search: drillThroughSearch(currentSearch, { runId, view: 'runs' }),
                      }}
                    >
                      Run {runId}
                    </Link>
                  </Button>
                ))}
              </div>
            )}
          </section>
          <details className="border-line rounded-lg border">
            <summary className="text-ink cursor-pointer px-4 py-3 text-sm font-semibold">Technical session evidence</summary>
            <div className="border-line border-t p-4">
              <JsonBlock
                value={{
                  clientErrors: detail.clientErrors ?? [],
                  pageViews: detail.pageViews ?? [],
                  relatedFacts: detail.relatedFacts ?? [],
                  routeNavigations: detail.routeNavigations ?? [],
                  sessionAttributes: session.attributes ?? {},
                  webVitals: detail.webVitals ?? [],
                }}
              />
            </div>
          </details>
        </div>
      ) : null}
    </Drawer>
  );
};

export const FrontendExperiencePage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { setState, state } = useRouteContext();
  const summaryQuery = useSummaryQuery(state);
  const sessionSearchQuery = useSessionSearchQuery(state);
  const sessionDetailQuery = useSessionDetailQuery(state);
  if (summaryQuery.isLoading) return <LoadingState label="Loading frontend experience evidence" />;
  if (summaryQuery.isError) {
    return <ErrorState message={summaryQuery.error.message} onRetry={() => void summaryQuery.refetch()} />;
  }

  const browserEvidence = summaryQuery.data?.signalEvidence?.find((evidence) => evidence.signal === 'browser_sessions');
  const rum = summaryQuery.data?.rum;
  const hasBrowserEvidence = (browserEvidence?.observations ?? 0) > 0;
  const clientErrors = rum?.clientErrors ?? 0;
  const vitals = rum?.webVitalSummaries ?? [];
  let samplingSummary = 'Sampling rate evidence is unavailable for this window.';
  if (summaryQuery.data?.rum.samplingConfigurationMixed) {
    samplingSummary = 'Multiple session sampling rates are present in this window.';
  } else if (typeof summaryQuery.data?.rum.samplingRate === 'number') {
    samplingSummary = `Sessions were sampled at ${(summaryQuery.data.rum.samplingRate * 100).toFixed(0)}%; counts are not extrapolated.`;
  }

  return (
    <div className="grid min-w-0 gap-6">
      <SessionDetailDrawer
        close={() =>
          void navigate(
            {
              pathname: '/frontend-experience',
              search: updateSearch(location.search, { sessionId: null }),
            },
            { replace: true },
          )
        }
        currentSearch={location.search}
        query={sessionDetailQuery}
        sessionId={state.sessionId}
      />

      <StateView
        title={
          hasBrowserEvidence
            ? `Browser collection received ${formatInteger(browserEvidence?.observations)} observations`
            : 'Browser collection has no observations in this scope'
        }
        tone={hasBrowserEvidence ? 'healthy' : 'coverage'}
      >
        {browserEvidence?.lastReceivedAt
          ? `Last received ${formatRelativeTime(browserEvidence.lastReceivedAt)} (${formatTime(browserEvidence.lastReceivedAt)}).`
          : 'No last-received browser event is available, so a zero cannot be interpreted as healthy.'}
      </StateView>

      <section className="grid gap-4 lg:grid-cols-[minmax(0,1.35fr)_minmax(18rem,0.65fr)]">
        <Card className={clientErrors > 0 ? 'border-critical/25 bg-critical-wash' : undefined}>
          <CardContent className="grid gap-4">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div>
                <div className="text-critical flex items-center gap-2 text-sm font-semibold">
                  <TriangleAlert aria-hidden="true" className="size-4" />
                  Primary urgent signal
                </div>
                <p className="text-ink numeric mt-2 text-4xl font-semibold">{formatInteger(clientErrors)}</p>
                <p className="text-muted mt-1 text-sm">
                  {clientErrors > 0 ? 'Client errors recorded in the selected scope.' : 'No client errors recorded in the selected scope.'}
                </p>
              </div>
              {clientErrors > 0 ? (
                <Button onClick={() => setState({ sessionErrorStatus: 'with', sessionOffset: 0 })}>Investigate affected sessions</Button>
              ) : null}
            </div>
            {!hasBrowserEvidence ? (
              <p className="text-warning text-xs">
                Browser collection evidence is missing; the zero-error count is not proof of a healthy frontend.
              </p>
            ) : null}
          </CardContent>
        </Card>
        <Card>
          <CardContent className="grid grid-cols-3 gap-3">
            <div>
              <p className="text-muted text-xs font-semibold">Sessions</p>
              <p className="text-ink numeric mt-1 text-xl font-semibold">{formatInteger(rum?.sessions)}</p>
            </div>
            <div>
              <p className="text-muted text-xs font-semibold">Page views</p>
              <p className="text-ink numeric mt-1 text-xl font-semibold">{formatInteger(rum?.pageViews)}</p>
            </div>
            <div>
              <p className="text-muted text-xs font-semibold">Vitals</p>
              <p className="text-ink numeric mt-1 text-xl font-semibold">{formatInteger(rum?.webVitals)}</p>
            </div>
          </CardContent>
        </Card>
      </section>

      <section className="grid gap-3">
        <div className="flex items-center gap-2">
          <MonitorSmartphone aria-hidden="true" className="text-accent size-4" />
          <div>
            <h2 className="text-ink text-base font-semibold">Route experience</h2>
            <p className="text-muted mt-1 text-sm">
              Core Web Vitals by route with P75 threshold semantics and observed sample counts. {samplingSummary}
            </p>
          </div>
        </div>
        <VitalHealth summaries={vitals} />
      </section>

      <Sessions currentSearch={location.search} key={state.search} query={sessionSearchQuery} setState={setState} state={state} />

      <div className="text-muted flex flex-wrap items-center gap-x-4 gap-y-1 text-xs">
        <span>
          Browser sessions: {formatInteger(browserEvidence?.observations)} observations
          {browserEvidence?.lastObservedAt ? ` · latest ${formatRelativeTime(browserEvidence.lastObservedAt)}` : ''}
        </span>
        <span>Generated {formatTime(summaryQuery.data?.generatedAt)}</span>
      </div>
    </div>
  );
};
